/**
 * GazzettaPage — `/gazzetta`, la "Gazzetta dello Squad" (Andrea 2026-09-24).
 *
 * Quattro schede per giornata:
 *   - Prima pagina: le pagine di giornale "simulato" caricate dai presidenti
 *     (immagini, compresse dall'app come i loghi);
 *   - Report: documenti Word caricati (il testo lo estrae il server) o
 *     testo incollato;
 *   - Dichiarazioni: le frasi alla stampa di ogni presidente;
 *   - Top 11: la miglior formazione possibile della giornata (Serie A e
 *     solo rose FantaSquad) e il Top manager.
 *
 * Si apre sull'ultima giornata calcolata; con le frecce si va alle
 * precedenti e alle successive. Caricano tutti; cancella l'autore o un admin.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useSwipe } from "../../hooks/useSwipe";
import { useMieSquadre, useSquadre } from "../../hooks/useFase7";
import { useStagioneCorrente } from "../../hooks/useStagione";
import {
  useCaricaPrimaPagina,
  useAggiornaPrimaPagina,
  caricaDatiPrimaPagina,
  type DatiGeneratore,
  useCaricaReport,
  useEliminaContenutoGazzetta,
  useGazzetta,
  useNuovaDichiarazione,
  useTop11,
  type DichiarazioneItem,
  type GiocatoreTopItem,
  type PrimaPaginaItem,
  type ReportItem,
  type UndiciItem,
} from "../../hooks/useGazzetta";
import { api } from "../../lib/api";
import { useAuthStore } from "../../lib/auth-store";
import { MobileShell } from "../shell/MobileShell";
import { LogoSquadra } from "../common/LogoSquadra";
import { compressImageToDataUrl } from "../common/imageCompress";
import { Pitch, type SlotState, type VotoLive } from "../formazione/Pitch";
import { MODULI_DISPONIBILI, type Modulo } from "../formazione/pitchLayouts";
import { etichetteSonoPro, siglaSlot } from "../common/nomenclatura";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
}

type Scheda = "prima" | "report" | "dichiarazioni" | "top11";

const SCHEDE: Array<{ id: Scheda; label: string; icon: string }> = [
  { id: "prima", label: tr("Prima pagina"), icon: "📰" },
  { id: "report", label: tr("Report"), icon: "📝" },
  { id: "dichiarazioni", label: tr("Dichiarazioni"), icon: "🎙" },
  { id: "top11", label: tr("Top 11"), icon: "🏆" },
];

const MAX_DICHIARAZIONE = 600;

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? tr("Errore");
}

function quando(iso: string): string {
  return new Date(iso).toLocaleString(localeCorrente(), {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statoLabel(stato: string): string {
  switch (stato) {
    case "calcolata":
      return tr("giornata calcolata");
    case "voti_caricati":
      return tr("voti caricati");
    case "annullata":
      return "annullata";
    default:
      return tr("da giocare");
  }
}

/** Il testo del report (estratto dal Word): titoli, elenchi, paragrafi. */
function TestoReport({ testo }: { testo: string }) {
  const blocchi = testo.split(/\n\s*\n/);
  return (
    <div className="space-y-2 text-sm text-fg leading-relaxed">
      {blocchi.map((b, i) => {
        const righe = b.split("\n");
        if (/^#{1,3} /.test(b)) {
          const livello = b.match(/^(#{1,3}) /)?.[1].length ?? 1;
          const t = b.replace(/^#{1,3} /, "");
          const cls =
            livello === 1
              ? "font-display text-lg text-accent"
              : livello === 2
                ? "font-display text-base text-accent"
                : "font-bold text-fg";
          return (
            <div key={i} className={`${cls} mt-2`}>
              {t}
            </div>
          );
        }
        if (righe.every((r) => r.startsWith("- "))) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-0.5">
              {righe.map((r, j) => (
                <li key={j}>{r.slice(2)}</li>
              ))}
            </ul>
          );
        }
        if (righe.length > 1 && righe.every((r) => r.includes(" | "))) {
          return (
            <div key={i} className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <tbody>
                  {righe.map((r, j) => (
                    <tr key={j} className="border-b border-primary/10">
                      {r.split(" | ").map((c, k) => (
                        <td key={k} className="px-2 py-1 align-top">
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            {b}
          </p>
        );
      })}
    </div>
  );
}

function Firma({
  autore,
  squadraId,
  nomeSquadra,
  creato,
  avatar,
}: {
  autore: string;
  squadraId: string | null;
  nomeSquadra: (id: string) => string;
  creato: string;
  /** avatar dell'autore (report e dichiarazioni, Andrea 2026-09-29) */
  avatar?: string | null;
}) {
  // Andrea 2026-09-29: comanda l'utente (avatar + nome), la squadra viene dopo.
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-fg-muted min-w-0">
      {avatar && (
        <img
          src={avatar}
          alt=""
          aria-hidden
          className="w-[22px] h-[22px] rounded-full object-cover border border-accent/40 shrink-0"
        />
      )}
      {/* il nome dell'utente vince: si stringe la squadra, non lui */}
      <b className="text-fg truncate shrink-0 max-w-[60%]">{autore}</b>
      {squadraId && (
        <span className="flex items-center gap-1 min-w-0">
          <span className="shrink-0">·</span>
          <LogoSquadra squadraId={squadraId} size={14} />
          <span className="truncate">{nomeSquadra(squadraId)}</span>
        </span>
      )}
      <span className="shrink-0">· {quando(creato)}</span>
    </div>
  );
}

function BottoneElimina({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="shrink-0 text-[10px] uppercase tracking-wider text-danger hover:underline disabled:opacity-50"
    >
      {tr("✕ Togli")}
    </button>
  );
}

/** Scelta della squadra con cui firmare, solo se se ne gestisce piu' d'una. */
function SceltaSquadra({
  squadre,
  value,
  onChange,
}: {
  squadre: Array<{ id: string; nome: string }>;
  value: string;
  onChange: (id: string) => void;
}) {
  if (squadre.length <= 1) return null;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg focus:border-accent/60 outline-none"
    >
      {squadre.map((s) => (
        <option key={s.id} value={s.id}>
          {s.nome}
        </option>
      ))}
    </select>
  );
}

// ============================================================
// Top 11
// ============================================================
function slotDaTop(g: GiocatoreTopItem): SlotState {
  return {
    giocatore_id: g.giocatore_id,
    nome: g.nome,
    ruoli_mantra: g.ruoli_mantra.length > 0 ? g.ruoli_mantra : null,
    ruolo_classic: g.ruolo_classic,
  };
}

function votoDaTop(g: GiocatoreTopItem): VotoLive {
  return {
    voto_base: g.voto_base,
    voto_finale: g.fantavoto,
    gol_fatti: g.gol_fatti,
    gol_subiti: g.gol_subiti,
    autogol: g.autogol,
    ammonizioni: g.ammonizioni,
    espulsioni: g.espulsioni,
    assist: g.assist,
    porta_inviolata: g.porta_inviolata,
    rigore_segnato: g.rigore_segnato,
    rigore_sbagliato: g.rigore_sbagliato,
    rigore_parato: g.rigore_parato,
  };
}

function Undici({
  titolo,
  sottotitolo,
  u,
  nomeSquadra,
}: {
  titolo: string;
  sottotitolo: string;
  u: UndiciItem | null;
  nomeSquadra: (id: string) => string;
}) {
  if (!u) {
    return (
      <div className="card text-sm text-fg-muted">
        {titolo}: non ci sono abbastanza giocatori con voto per fare un undici.
      </div>
    );
  }
  const modulo: Modulo = (MODULI_DISPONIBILI as readonly string[]).includes(u.modulo)
    ? (u.modulo as Modulo)
    : "4-3-3";
  const voti = new Map<string, VotoLive>(u.giocatori.map((g) => [g.giocatore_id, votoDaTop(g)]));
  // Nomenclatura Pro (29/09): sigle tradotte solo se il modulo e' Pro.
  const slotPro = etichetteSonoPro(u.slot_labels);
  return (
    <div className="card space-y-2 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display text-lg text-accent">{titolo}</div>
          <div className="text-[11px] text-fg-muted">{sottotitolo}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl tabular-nums leading-none">
            {Number(u.totale).toFixed(1)}
          </div>
          <div className="text-[9px] text-fg-muted font-mono uppercase">
            fantapunti · {u.modulo}
          </div>
        </div>
      </div>
      <Pitch
        modulo={modulo}
        slots={u.giocatori.map(slotDaTop)}
        capitanoId=""
        viceId=""
        slotLabels={u.slot_labels}
        votiPerGiocatore={voti}
        onDropToSlot={() => undefined}
        onClickPlayer={() => undefined}
      />
      <div className="divide-y divide-primary/10">
        {u.giocatori.map((g) => (
          <div key={g.giocatore_id} className="flex items-center gap-2 py-1 text-[12px]">
            <span className="w-9 shrink-0 font-mono text-[10px] text-fg-muted">
              {siglaSlot(g.slot_ruolo, slotPro)}
            </span>
            <span className="flex-1 min-w-0 truncate">
              <b>{g.nome}</b> <span className="text-fg-muted">{g.club_serie_a ?? ""}</span>
            </span>
            <span className="flex items-center gap-1 min-w-0 text-[11px] text-fg-muted truncate">
              {g.squadra_id ? (
                <>
                  <LogoSquadra squadraId={g.squadra_id} size={16} />
                  <span className="truncate">{nomeSquadra(g.squadra_id)}</span>
                </>
              ) : (
                <span className="italic">{tr("svincolato")}</span>
              )}
            </span>
            <span className="w-10 shrink-0 text-right font-mono font-bold text-accent tabular-nums">
              {Number(g.fantavoto).toFixed(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const UNDICI = [
  {
    key: "fantasquad" as const,
    titolo: tr("Top 11 FantaSquad"),
    sottotitolo: tr("Il miglior undici tra i giocatori in rosa nella lega"),
  },
  {
    key: "serie_a" as const,
    titolo: tr("Top 11 Serie A"),
    sottotitolo: tr("Il miglior undici assoluto della giornata"),
  },
];

function SchedaTop11({
  giornataId,
  nomeSquadra,
}: {
  giornataId: string;
  nomeSquadra: (id: string) => string;
}) {
  const { data, isLoading, error } = useTop11(giornataId);
  const [indice, setIndice] = useState(0);
  const vaiA = (delta: number) => setIndice((i) => (i + delta + UNDICI.length) % UNDICI.length);
  const swipe = useSwipe({ onAvanti: () => vaiA(1), onIndietro: () => vaiA(-1) });
  if (isLoading)
    return <div className="card text-fg-muted text-sm">{tr("Calcolo il Top 11…")}</div>;
  if (error) return <div className="card text-danger text-sm">{errMsg(error)}</div>;
  if (!data) return null;
  if (!data.disponibile) {
    return <div className="card text-fg-muted text-sm text-center">{data.motivo}</div>;
  }
  return (
    <div className="space-y-3">
      {data.top_manager.length > 0 && (
        <div className="rounded-card border border-accent/50 bg-accent/10 px-4 py-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-accent mb-2">
            {tr("🏆 Top manager della giornata")}
          </div>
          <div className="space-y-1.5">
            {data.top_manager.map((t) => (
              <div key={t.squadra_id} className="flex items-center gap-2">
                <LogoSquadra squadraId={t.squadra_id} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-tight truncate">{t.nome}</div>
                  <div className="text-[11px] text-fg-muted">{t.gol} gol</div>
                </div>
                <div className="font-display text-2xl tabular-nums text-accent">
                  {Number(t.punteggio).toFixed(1)}
                </div>
              </div>
            ))}
          </div>
          {data.top_manager.length > 1 && (
            <div className="text-[10px] text-fg-muted mt-1">
              {tr("A pari punteggio: ex aequo.")}
            </div>
          )}
        </div>
      )}
      {/* Andrea 2026-09-24: su desktop i due undici uno accanto all'altro;
          su mobile uno per volta, si sfoglia con le frecce o trascinando. */}
      <div className="hidden md:grid grid-cols-2 gap-3 items-start">
        {UNDICI.map((u) => (
          <Undici
            key={u.key}
            titolo={u.titolo}
            sottotitolo={u.sottotitolo}
            u={u.key === "fantasquad" ? data.fantasquad : data.serie_a}
            nomeSquadra={nomeSquadra}
          />
        ))}
      </div>
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={() => vaiA(-1)}
            aria-label={tr("Undici precedente")}
            className="w-7 h-7 rounded-full bg-primary-deep/85 border border-accent/40 text-accent leading-none flex items-center justify-center hover:bg-primary-deep transition"
          >
            ‹
          </button>
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
            {UNDICI[indice].titolo} · {indice + 1}/{UNDICI.length}
          </span>
          <button
            type="button"
            onClick={() => vaiA(1)}
            aria-label={tr("Undici successivo")}
            className="w-7 h-7 rounded-full bg-primary-deep/85 border border-accent/40 text-accent leading-none flex items-center justify-center hover:bg-primary-deep transition"
          >
            ›
          </button>
        </div>
        <div
          {...swipe.handlers}
          className="touch-pan-y select-none"
          style={{
            transform: `translateX(${swipe.offset}px)`,
            transition: swipe.inTrascinamento ? "none" : "transform 150ms",
          }}
        >
          <Undici
            titolo={UNDICI[indice].titolo}
            sottotitolo={UNDICI[indice].sottotitolo}
            u={UNDICI[indice].key === "fantasquad" ? data.fantasquad : data.serie_a}
            nomeSquadra={nomeSquadra}
          />
        </div>
        <div className="flex justify-center gap-1.5 mt-2">
          {UNDICI.map((u, i) => (
            <button
              key={u.key}
              type="button"
              onClick={() => setIndice(i)}
              aria-label={u.titolo}
              className={`h-1.5 rounded-full transition ${
                i === indice ? "w-6 bg-accent" : "w-3 bg-fg-muted/40"
              }`}
            />
          ))}
        </div>
      </div>
      <p className="text-[10px] text-fg-muted">
        Fantavoto = voto + bonus/malus del regolamento. Si provano tutti i moduli e si tiene
        l'undici col totale più alto.
      </p>
    </div>
  );
}

// ============================================================
// Pagina
// ============================================================
export function GazzettaPage() {
  const { user } = useAuth();
  const isAuth = useAuthStore((s) => !!s.accessToken);
  const { data: stagione } = useStagioneCorrente();
  const stagioneId = stagione?.id ?? null;
  const { data: squadre } = useSquadre();
  const { data: mieSquadre } = useMieSquadre(user?.id);
  const nomeSquadra = (id: string): string =>
    squadre?.find((s) => s.id === id)?.nome ?? id.slice(0, 8);

  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate", stagioneId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: isAuth && !!stagioneId,
  });
  const ordinate = useMemo(
    () => [...(giornate ?? [])].sort((a, b) => a.numero - b.numero),
    [giornate],
  );
  // Si apre sull'ultima giornata calcolata (o l'ultima con qualcosa), non
  // sulla prossima da giocare: la stampa parla di quel che e' successo.
  // Le notifiche arrivano con ?g=<giornata>&s=<scheda>: si apre li'.
  const [params] = useSearchParams();
  const gParam = params.get("g");
  const sParam = params.get("s");
  const [indice, setIndice] = useState<number | null>(null);
  useEffect(() => {
    if (indice !== null || ordinate.length === 0) return;
    let i = gParam ? ordinate.findIndex((g) => g.id === gParam) : -1;
    for (let k = ordinate.length - 1; i < 0 && k >= 0; k--) {
      if (ordinate[k].stato === "calcolata" || ordinate[k].stato === "voti_caricati") {
        i = k;
        break;
      }
    }
    setIndice(i >= 0 ? i : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordinate, indice]);
  const giornata = indice !== null ? (ordinate[indice] ?? null) : null;
  const giornataId = giornata?.id ?? null;

  const [scheda, setScheda] = useState<Scheda>(() =>
    sParam === "report" || sParam === "dichiarazioni" || sParam === "top11" ? sParam : "prima",
  );
  const { data: gazzetta, isLoading } = useGazzetta(giornataId);
  const elimina = useEliminaContenutoGazzetta(giornataId);
  const posso = (userId: string) => !!gazzetta && (gazzetta.posso_moderare || user?.id === userId);

  // Squadra con cui firmare.
  const mie = mieSquadre ?? [];
  const [squadraFirma, setSquadraFirma] = useState<string>("");
  useEffect(() => {
    if (!squadraFirma && (mie[0]?.id || gazzetta?.mia_squadra_id)) {
      setSquadraFirma(mie[0]?.id ?? gazzetta?.mia_squadra_id ?? "");
    }
  }, [mie, gazzetta, squadraFirma]);

  const [errore, setErrore] = useState<string | null>(null);
  const togli = async (tipo: "prime-pagine" | "report" | "dichiarazioni", id: string) => {
    if (!confirm(tr("Togliere questo contenuto?"))) return;
    setErrore(null);
    try {
      await elimina.mutateAsync({ tipo, id });
    } catch (e) {
      setErrore(errMsg(e));
    }
  };

  return (
    <MobileShell logoSuffix="MAGAZINE">
      <div className="bg-gradient-to-br from-bg-card to-primary-deep border border-accent/30 rounded-card p-4 flex items-center gap-3">
        <div className="text-3xl">📰</div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-accent tracking-wider">SQUAD MAGAZINE</div>
          <div className="text-xs text-fg-muted">
            {tr("Prime pagine, report, dichiarazioni e il Top 11 di ogni giornata")}
          </div>
        </div>
      </div>

      {/* Giornata */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setIndice((i) => Math.max(0, (i ?? 0) - 1))}
          disabled={indice === null || indice === 0}
          aria-label={tr("Giornata precedente")}
          className="w-8 h-8 rounded-full bg-primary-deep/85 border border-accent/40 text-accent text-lg leading-none flex items-center justify-center hover:bg-primary-deep transition disabled:opacity-30"
        >
          ‹
        </button>
        <div className="text-center min-w-0">
          <div className="font-display text-lg text-fg">
            {giornata ? tr("Giornata {{v1}}", { v1: giornata.numero }) : "—"}
          </div>
          {giornata && (
            <div className="text-[10px] font-mono uppercase tracking-wider text-fg-muted">
              SA {giornata.numero_serie_a} · {statoLabel(giornata.stato)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIndice((i) => Math.min(ordinate.length - 1, (i ?? 0) + 1))}
          disabled={indice === null || indice >= ordinate.length - 1}
          aria-label={tr("Giornata successiva")}
          className="w-8 h-8 rounded-full bg-primary-deep/85 border border-accent/40 text-accent text-lg leading-none flex items-center justify-center hover:bg-primary-deep transition disabled:opacity-30"
        >
          ›
        </button>
      </div>

      {/* Schede */}
      <div className="grid grid-cols-4 gap-1">
        {SCHEDE.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setScheda(s.id)}
            className={`rounded-card px-1 py-2 text-[11px] sm:text-xs font-bold leading-tight transition ${
              scheda === s.id
                ? "bg-accent text-ink"
                : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
            }`}
          >
            <span className="block text-base leading-none mb-0.5">{s.icon}</span>
            {s.label}
          </button>
        ))}
      </div>

      {errore && <div className="text-xs text-danger">⚠ {errore}</div>}

      {!stagioneId && <div className="card text-fg-muted text-sm">{tr("Nessuna stagione.")}</div>}
      {giornataId && isLoading && (
        <div className="card text-fg-muted text-sm">{tr("Caricamento…")}</div>
      )}

      {gazzetta && giornata && scheda === "prima" && (
        <SchedaPrimaPagina
          giornataId={giornata.id}
          giornata={giornata}
          stagioneAnno={stagione?.anno ?? null}
          pagine={gazzetta.prime_pagine}
          nomeSquadra={nomeSquadra}
          posso={posso}
          onTogli={(id) => togli("prime-pagine", id)}
          eliminaBusy={elimina.isPending}
          mie={mie}
          squadraFirma={squadraFirma}
          setSquadraFirma={setSquadraFirma}
        />
      )}
      {gazzetta && giornata && scheda === "report" && (
        <SchedaReport
          giornataId={giornata.id}
          giornata={giornata}
          stagioneAnno={stagione?.anno ?? null}
          report={gazzetta.report}
          avatarAutori={gazzetta.avatar_autori}
          nomeSquadra={nomeSquadra}
          posso={posso}
          onTogli={(id) => togli("report", id)}
          eliminaBusy={elimina.isPending}
          mie={mie}
          squadraFirma={squadraFirma}
          setSquadraFirma={setSquadraFirma}
        />
      )}
      {gazzetta && giornata && scheda === "dichiarazioni" && (
        <SchedaDichiarazioni
          giornataId={giornata.id}
          giornata={giornata}
          stagioneAnno={stagione?.anno ?? null}
          dichiarazioni={gazzetta.dichiarazioni}
          avatarAutori={gazzetta.avatar_autori}
          nomeSquadra={nomeSquadra}
          posso={posso}
          onTogli={(id) => togli("dichiarazioni", id)}
          eliminaBusy={elimina.isPending}
          mie={mie}
          squadraFirma={squadraFirma}
          setSquadraFirma={setSquadraFirma}
        />
      )}
      {giornataId && scheda === "top11" && (
        <SchedaTop11 giornataId={giornataId} nomeSquadra={nomeSquadra} />
      )}
    </MobileShell>
  );
}

interface SchedaProps {
  giornataId: string;
  giornata: GiornataLight;
  stagioneAnno: string | null;
  nomeSquadra: (id: string) => string;
  posso: (userId: string) => boolean;
  onTogli: (id: string) => void;
  eliminaBusy: boolean;
  mie: Array<{ id: string; nome: string }>;
  squadraFirma: string;
  setSquadraFirma: (id: string) => void;
  /** avatar degli autori (user_id -> data-URL) */
  avatarAutori?: Record<string, string>;
}

// ============================================================
// Generatore di prima pagina (Andrea 2026-09-24): il suo gazzetta.html,
// servito da /public, aperto a schermo intero in un iframe. Al "Salva"
// manda l'immagine con postMessage; qui la si ricomprime sotto il limite
// e la si pubblica per la giornata selezionata.
// ============================================================
interface PayloadGeneratore {
  date?: string;
  season?: string;
  matchday?: string;
  hero?: { title?: string };
  image: string;
  /** i campi compilati, per la modifica (2026-10-15) */
  dati?: DatiGeneratore;
}

function GeneratoreModal({
  giornata,
  stagioneAnno,
  datiIniziali,
  onSalva,
  onClose,
}: {
  giornata: GiornataLight;
  stagioneAnno: string | null;
  /** Andrea 2026-10-15: modifica di una pagina salvata: si riparte dai suoi campi */
  datiIniziali?: DatiGeneratore | null;
  onSalva: (
    immagineDataUrl: string,
    titolo: string | undefined,
    dati: DatiGeneratore | undefined,
  ) => Promise<void>;
  onClose: () => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [stato, setStato] = useState<"pronto" | "salvo" | "fatto" | "errore">("pronto");
  const [errore, setErrore] = useState<string | null>(null);

  const prefill = () => {
    // Il generatore ha un calendario: la data va in ISO (aaaa-mm-gg, ora locale).
    const d = new Date();
    const data = tr("{{v1}}-{{v2}}-{{v3}}", {
      v1: d.getFullYear(),
      v2: String(d.getMonth() + 1).padStart(2, "0"),
      v3: String(d.getDate()).padStart(2, "0"),
    });
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: "gazzetta:prefill",
        payload: {
          date: data,
          season: stagioneAnno ?? "",
          matchday: tr("Giornata {{v1}}", { v1: giornata.numero }),
          dati: datiIniziali ?? undefined,
        },
      },
      window.location.origin,
    );
  };

  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const msg = e.data as { type?: string; payload?: PayloadGeneratore } | null;
      if (!msg?.type) return;
      if (msg.type === "gazzetta:ready") {
        prefill();
        return;
      }
      if (msg.type === "gazzetta:save" && msg.payload?.image) {
        setStato("salvo");
        setErrore(null);
        try {
          await onSalva(
            msg.payload.image,
            msg.payload.hero?.title?.trim() || undefined,
            msg.payload.dati,
          );
          setStato("fatto");
        } catch (err) {
          setErrore(errMsg(err));
          setStato("errore");
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giornata.id, stagioneAnno]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-primary-deep border-b border-accent/30">
        <div className="min-w-0">
          <div className="font-display text-accent text-base leading-tight">
            {datiIniziali ? tr("Modifica prima pagina") : tr("Squad Magazine · prima pagina")} ·{" "}
            {tr("Giornata {{v1}}", { v1: giornata.numero })}
          </div>
          <div className="text-[10px] text-fg-muted">
            {stato === "pronto" && tr("Compila, poi premi «Salva prima pagina» nel pannello")}
            {stato === "salvo" && tr("Comprimo e pubblico la prima pagina…")}
            {stato === "fatto" && tr("✓ Pubblicata! Puoi chiudere o farne un'altra.")}
            {stato === "errore" && tr("⚠ {{v1}}", { v1: errore })}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 px-3 py-1.5 rounded-card border border-accent/40 text-accent text-sm hover:bg-accent/10"
        >
          {stato === "fatto" ? tr("Chiudi") : tr("✕ Chiudi")}
        </button>
      </div>
      <iframe
        ref={iframeRef}
        src="/gazzetta/gazzetta.html"
        title={tr("Squad Magazine · generatore prima pagina")}
        className="flex-1 w-full bg-[#e7e2da]"
        onLoad={prefill}
      />
    </div>
  );
}

/** Un data-URL (il PNG di html2canvas, anche 5-8 MB) → ridimensionato e
 * compresso sotto il limite del server, riusando la pipeline dei loghi. */
async function comprimiDataUrl(dataUrl: string): Promise<string> {
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], "prima-pagina.png", { type: blob.type || "image/png" });
  return compressImageToDataUrl(file, { maxDim: 1600, maxBytes: 1_400_000 });
}

// ============================================================
// Prima pagina
// ============================================================
function SchedaPrimaPagina({
  giornataId,
  giornata,
  stagioneAnno,
  pagine,
  nomeSquadra,
  posso,
  onTogli,
  eliminaBusy,
  mie,
  squadraFirma,
  setSquadraFirma,
}: SchedaProps & { pagine: PrimaPaginaItem[] }) {
  const carica = useCaricaPrimaPagina(giornataId);
  const aggiorna = useAggiornaPrimaPagina(giornataId);
  const [titolo, setTitolo] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  // null = chiuso; paginaId null = nuova; con paginaId = modifica (2026-10-15)
  const [generatore, setGeneratore] = useState<{
    paginaId: string | null;
    dati: DatiGeneratore | null;
  } | null>(null);
  const [modificaInCorso, setModificaInCorso] = useState<string | null>(null);

  const apriModifica = async (p: PrimaPaginaItem) => {
    setErrore(null);
    setModificaInCorso(p.id);
    try {
      const dati = await caricaDatiPrimaPagina(p.id);
      setGeneratore({ paginaId: p.id, dati });
    } catch (e) {
      setErrore(errMsg(e));
    } finally {
      setModificaInCorso(null);
    }
  };

  const invia = async () => {
    if (!file) return;
    setErrore(null);
    setInCorso(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxDim: 1600, maxBytes: 1_400_000 });
      await carica.mutateAsync({
        immagineDataUrl: dataUrl,
        titolo: titolo.trim() || undefined,
        squadraId: squadraFirma || null,
      });
      setTitolo("");
      setFile(null);
    } catch (e) {
      setErrore(errMsg(e));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className="space-y-3">
      {pagine.length === 0 && (
        <div className="card text-center text-fg-muted text-sm">
          {tr("Nessuna prima pagina per questa giornata. Sii il primo a stamparla!")}
        </div>
      )}
      {pagine.map((p) => (
        <div key={p.id} className="card p-2 space-y-2 md:max-w-4xl md:mx-auto">
          <img
            src={p.immagine_data_url}
            alt={p.titolo ?? tr("Prima pagina")}
            className="w-full rounded-card border border-primary/20"
            loading="lazy"
          />
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="min-w-0">
              {p.titolo && (
                <div className="font-display text-base text-fg truncate">{p.titolo}</div>
              )}
              <Firma
                autore={p.autore}
                squadraId={p.squadra_id}
                nomeSquadra={nomeSquadra}
                creato={p.created_at}
              />
            </div>
            {posso(p.user_id) && (
              <div className="flex items-center gap-3 shrink-0">
                {p.ha_dati && (
                  <button
                    type="button"
                    onClick={() => void apriModifica(p)}
                    disabled={modificaInCorso === p.id}
                    className="text-[10px] uppercase tracking-wider text-accent hover:underline disabled:opacity-50"
                    title={tr("Riapri nel generatore e correggi")}
                  >
                    {modificaInCorso === p.id ? tr("Apro…") : tr("✎ Modifica")}
                  </button>
                )}
                <BottoneElimina onClick={() => onTogli(p.id)} busy={eliminaBusy} />
              </div>
            )}
          </div>
        </div>
      ))}

      <div className="card space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
          {tr("Crea la prima pagina")}
        </div>
        <SceltaSquadra squadre={mie} value={squadraFirma} onChange={setSquadraFirma} />
        <button
          type="button"
          onClick={() => setGeneratore({ paginaId: null, dati: null })}
          className="w-full rounded-card px-4 py-3 bg-accent text-ink font-bold tracking-wide hover:opacity-90 transition"
        >
          {tr("✍️ Apri il generatore di prima pagina")}
        </button>
        <div className="text-[10px] text-fg-muted">
          Titolone, notizia principale, colonne e foto: la pagina si compone da sola e al «Salva»
          viene pubblicata qui per la giornata {giornata.numero}.
        </div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted pt-1">
          {tr("…oppure carica un'immagine già pronta")}
        </div>
        <input
          type="text"
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
          maxLength={120}
          placeholder={tr("Titolo (facoltativo)")}
          className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg focus:border-accent/60 outline-none"
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-xs text-fg-muted file:mr-2 file:rounded-card file:border file:border-accent/40 file:bg-accent/10 file:px-3 file:py-1.5 file:text-accent file:text-xs"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-fg-muted">
            {tr("PNG o JPG: viene ridimensionata e compressa dall'app.")}
          </span>
          <button
            type="button"
            onClick={invia}
            disabled={!file || inCorso}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {inCorso ? "Carico…" : "Pubblica"}
          </button>
        </div>
        {errore && <div className="text-[11px] text-danger">⚠ {errore}</div>}
      </div>
      {generatore && (
        <GeneratoreModal
          giornata={giornata}
          stagioneAnno={stagioneAnno}
          datiIniziali={generatore.dati}
          onClose={() => setGeneratore(null)}
          onSalva={async (immagine, titoloHero, dati) => {
            const dataUrl = await comprimiDataUrl(immagine);
            if (generatore.paginaId) {
              await aggiorna.mutateAsync({
                paginaId: generatore.paginaId,
                immagineDataUrl: dataUrl,
                titolo: titoloHero,
                dati: dati ?? null,
              });
              return;
            }
            await carica.mutateAsync({
              immagineDataUrl: dataUrl,
              titolo: titoloHero,
              squadraId: squadraFirma || null,
              dati: dati ?? null,
            });
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Report
// ============================================================
function SchedaReport({
  giornataId,
  report,
  nomeSquadra,
  posso,
  onTogli,
  eliminaBusy,
  mie,
  squadraFirma,
  setSquadraFirma,
  avatarAutori,
}: SchedaProps & { report: ReportItem[] }) {
  const carica = useCaricaReport(giornataId);
  const [titolo, setTitolo] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [testo, setTesto] = useState("");
  const [aperti, setAperti] = useState<Set<string>>(new Set());
  const [errore, setErrore] = useState<string | null>(null);

  const invia = async () => {
    setErrore(null);
    try {
      await carica.mutateAsync({
        titolo: titolo.trim(),
        file,
        testo: file ? undefined : testo.trim(),
        squadraId: squadraFirma || null,
      });
      setTitolo("");
      setFile(null);
      setTesto("");
    } catch (e) {
      setErrore(errMsg(e));
    }
  };
  const toggle = (id: string) =>
    setAperti((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="space-y-3">
      {report.length === 0 && (
        <div className="card text-center text-fg-muted text-sm">
          {tr("Nessun report per questa giornata.")}
        </div>
      )}
      {report.map((r, i) => {
        const aperto = aperti.has(r.id) || (i === 0 && aperti.size === 0);
        return (
          <div key={r.id} className="card space-y-2">
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                onClick={() => toggle(r.id)}
                className="text-left min-w-0 flex-1"
                aria-expanded={aperto}
              >
                <div className="font-display text-lg text-accent leading-tight">{r.titolo}</div>
                <Firma
                  autore={r.autore}
                  squadraId={r.squadra_id}
                  nomeSquadra={nomeSquadra}
                  creato={r.created_at}
                  avatar={avatarAutori?.[r.user_id]}
                />
              </button>
              <div className="flex items-center gap-2 shrink-0">
                {posso(r.user_id) && (
                  <BottoneElimina onClick={() => onTogli(r.id)} busy={eliminaBusy} />
                )}
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  aria-label={aperto ? "Chiudi" : "Leggi"}
                  className={`text-accent text-xs transition-transform ${aperto ? "rotate-180" : ""}`}
                >
                  ▾
                </button>
              </div>
            </div>
            {aperto && (
              <div className="border-t border-primary/10 pt-2">
                <TestoReport testo={r.testo} />
                {r.nome_file && (
                  <div className="text-[10px] text-fg-muted mt-2">Da {r.nome_file}</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="card space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
          {tr("Pubblica un report")}
        </div>
        <SceltaSquadra squadre={mie} value={squadraFirma} onChange={setSquadraFirma} />
        <input
          type="text"
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
          maxLength={160}
          placeholder={tr("Titolo")}
          className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg focus:border-accent/60 outline-none"
        />
        <input
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-xs text-fg-muted file:mr-2 file:rounded-card file:border file:border-accent/40 file:bg-accent/10 file:px-3 file:py-1.5 file:text-accent file:text-xs"
        />
        {!file && (
          <textarea
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            rows={5}
            placeholder={tr("…oppure incolla qui il testo del report")}
            className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg focus:border-accent/60 outline-none"
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-fg-muted">
            {tr("Documento Word (.docx): il testo viene estratto e mostrato qui.")}
          </span>
          <button
            type="button"
            onClick={invia}
            disabled={carica.isPending || !titolo.trim() || (!file && !testo.trim())}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {carica.isPending ? "Pubblico…" : "Pubblica"}
          </button>
        </div>
        {errore && <div className="text-[11px] text-danger">⚠ {errore}</div>}
      </div>
    </div>
  );
}

// ============================================================
// Dichiarazioni
// ============================================================
function SchedaDichiarazioni({
  giornataId,
  dichiarazioni,
  nomeSquadra,
  posso,
  onTogli,
  eliminaBusy,
  mie,
  squadraFirma,
  setSquadraFirma,
  avatarAutori,
}: SchedaProps & { dichiarazioni: DichiarazioneItem[] }) {
  const nuova = useNuovaDichiarazione(giornataId);
  const [testo, setTesto] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const invia = async () => {
    setErrore(null);
    try {
      await nuova.mutateAsync({ testo: testo.trim(), squadraId: squadraFirma });
      setTesto("");
    } catch (e) {
      setErrore(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <div className="card space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
          {tr("🎙 La tua dichiarazione alla stampa")}
        </div>
        <SceltaSquadra squadre={mie} value={squadraFirma} onChange={setSquadraFirma} />
        <textarea
          value={testo}
          onChange={(e) => setTesto(e.target.value.slice(0, MAX_DICHIARAZIONE))}
          rows={3}
          placeholder={tr("Il mister ha dichiarato…")}
          className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg focus:border-accent/60 outline-none"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-fg-muted tabular-nums">
            {testo.length}/{MAX_DICHIARAZIONE}
            {!squadraFirma && " · serve una squadra con cui firmare"}
          </span>
          <button
            type="button"
            onClick={invia}
            disabled={nuova.isPending || !testo.trim() || !squadraFirma}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {nuova.isPending ? "…" : "Dichiara"}
          </button>
        </div>
        {errore && <div className="text-[11px] text-danger">⚠ {errore}</div>}
      </div>

      {dichiarazioni.length === 0 && (
        <div className="card text-center text-fg-muted text-sm">
          {tr("Nessuna dichiarazione: i presidenti sono in silenzio stampa.")}
        </div>
      )}
      {dichiarazioni.map((d) => (
        <div key={d.id} className="card space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Firma
              autore={d.autore}
              squadraId={d.squadra_id}
              nomeSquadra={nomeSquadra}
              creato={d.created_at}
              avatar={avatarAutori?.[d.user_id]}
            />
            {posso(d.user_id) && (
              <BottoneElimina onClick={() => onTogli(d.id)} busy={eliminaBusy} />
            )}
          </div>
          <blockquote className="text-sm text-fg leading-relaxed border-l-2 border-accent/50 pl-3 whitespace-pre-wrap">
            “{d.testo}”
          </blockquote>
        </div>
      ))}
    </div>
  );
}
